/** OrderGroove admin returns numeric day values (0, 14, 30, ...). */

type OgFrequencyIssueCode =
  | 'invalid_raw_frequency'
  | 'missing_frequency_source'
  | 'fallback_to_subscription_frequency'

enum OgEveryPeriod {
  Day = '1',
  Week = '2',
  Month = '3',
}

const periodLengthsInDays: Record<OgEveryPeriod, number> = {
  [OgEveryPeriod.Day]: 1,
  [OgEveryPeriod.Week]: 7,
  [OgEveryPeriod.Month]: 30,
}

function reportOgFrequencyIssue(
  code: OgFrequencyIssueCode,
  details: Record<string, unknown>
): void {
  if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
    window.dispatchEvent(
      new CustomEvent('og-frequency-issue', {
        detail: {
          code,
          ...details,
        },
      })
    )
  }
}

export function isOgFrequencyValue(value: string): boolean {
  return /^\d+$/.test(value.trim())
}

export function ogFrequencyToSubscriptionDays(raw: string): number {
  const trimmed = raw.trim()

  const everyPeriodMatch = trimmed.match(/^(\d+)_([123])$/)
  if (everyPeriodMatch) {
    const multiplier = Number(everyPeriodMatch[1])
    const period = everyPeriodMatch[2] as OgEveryPeriod
    return multiplier * periodLengthsInDays[period]
  }

  const numericPart = trimmed.match(/\d+/)?.[0]
  if (!numericPart) {
    reportOgFrequencyIssue('invalid_raw_frequency', { raw: trimmed })
    return 0
  }

  return Number(numericPart)
}

export function findOgOfferElement(
  ogOffer?: Element | null,
  location = 'PDP'
): Element | null {
  if (ogOffer) {
    return ogOffer
  }
  if (typeof document === 'undefined') {
    return null
  }
  return (
    document.querySelector(`og-offer[location="${location}"]`) ??
    document.querySelector('og-offer')
  )
}

function readOgFrequencyRaw(ogOffer: Element): string | null {
  const activeOptIn = ogOffer.querySelector(
    'og-optin-button[active], og-optin-button[selected], og-optin-button[aria-pressed="true"], og-optin-button[frequency-match]'
  )
  const optInFrequency = activeOptIn?.getAttribute('frequency')?.trim()
  if (optInFrequency) {
    return optInFrequency
  }

  const offerFrequency = ogOffer.getAttribute('frequency')?.trim()
  if (offerFrequency) {
    return offerFrequency
  }

  const select =
    ogOffer.querySelector('select[aria-label="Delivery frequency"]') ??
    ogOffer.querySelector('select[aria-label="Deliver every"]') ??
    ogOffer.querySelector('select')

  if (select instanceof HTMLSelectElement) {
    const value = select.value.trim()
    if (value) {
      return value
    }
  }

  const attr = ogOffer.getAttribute('frequency')?.trim()
  if (attr) {
    return attr
  }

  return null
}

export function getOrderGrooveFrequencyDays(ogOffer?: Element | null): number {
  if (!ogOffer || typeof ogOffer.getAttribute !== 'function') {
    return 0
  }

  if (ogOffer.getAttribute('subscribed') === null) {
    return 0
  }

  const raw = readOgFrequencyRaw(ogOffer)
  if (!raw) {
    reportOgFrequencyIssue('missing_frequency_source', {
      location: ogOffer.getAttribute('location') ?? 'unknown',
    })
    return 0
  }

  return ogFrequencyToSubscriptionDays(raw)
}

export function resolveAddToCartFrequency(options: {
  ogOffer?: Element | null
  ogOfferLocation?: string
  subscriptionFrequency?: number
}): number {
  const ogOffer = findOgOfferElement(
    options.ogOffer,
    options.ogOfferLocation ?? 'PDP'
  )
  const ogDays = getOrderGrooveFrequencyDays(ogOffer)
  if (ogDays > 0) {
    return ogDays
  }

  const subscription = options.subscriptionFrequency ?? 0
  const isSubscribed = ogOffer !== null && ogOffer.getAttribute('subscribed') !== null
  if (subscription > 0 && isSubscribed) {
    const rawFrequencyAttribute = ogOffer.getAttribute('frequency')
    reportOgFrequencyIssue('fallback_to_subscription_frequency', {
      ogOfferLocation: options.ogOfferLocation ?? 'PDP',
      subscriptionFrequency: subscription,
      rawFrequencyAttribute,
    })
  }
  return subscription > 0 ? subscription : 0
}
